import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "root", "backdrop", "panel", "phoneStep", "otpStep", "phone", "countryCode",
    "phoneLabel", "phoneError", "otpError", "otpDigit", "submitPhone", "submitOtp"
  ]

  connect() {
    this.returnTo = window.location.pathname + window.location.search

    this._onOpen = (e) => {
      if (e.detail && e.detail.returnTo) this.returnTo = e.detail.returnTo
      this.show()
    }
    this._onTrigger = (e) => {
      const trigger = e.target.closest("[data-login-trigger]")
      if (!trigger) return
      e.preventDefault()
      this.returnTo = trigger.dataset.returnTo || window.location.pathname + window.location.search
      this.show()
    }
    this._onKey = (e) => { if (e.key === "Escape") this.close() }

    window.addEventListener("login:open", this._onOpen)
    document.addEventListener("click", this._onTrigger)
  }

  disconnect() {
    window.removeEventListener("login:open", this._onOpen)
    document.removeEventListener("click", this._onTrigger)
    document.removeEventListener("keydown", this._onKey)
  }

  show() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
    this.rootTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this._onKey)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.hasBackdropTarget) {
          this.backdropTarget.classList.remove("opacity-0")
          this.backdropTarget.classList.add("opacity-100")
        }
        if (this.hasPanelTarget) {
          this.panelTarget.classList.remove("opacity-0", "scale-95")
          this.panelTarget.classList.add("opacity-100", "scale-100")
        }
      })
    })

    this.showPhoneStep()
    setTimeout(() => { if (this.hasPhoneTarget) this.phoneTarget.focus() }, 150)
  }

  close() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100")
      this.backdropTarget.classList.add("opacity-0")
    }
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("opacity-100", "scale-100")
      this.panelTarget.classList.add("opacity-0", "scale-95")
    }
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onKey)

    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => {
      this.rootTarget.classList.add("hidden")
      this.closeTimeout = null
    }, 300)
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.close()
  }

  showPhoneStep() {
    this.phoneStepTarget.classList.remove("hidden")
    this.otpStepTarget.classList.add("hidden")
    this._clearErrors()
  }

  showOtpStep() {
    this.phoneStepTarget.classList.add("hidden")
    this.otpStepTarget.classList.remove("hidden")
    this._clearErrors()
    this.otpDigitTargets.forEach(i => (i.value = ""))
    setTimeout(() => { if (this.otpDigitTargets[0]) this.otpDigitTargets[0].focus() }, 120)
  }

  backToPhone(event) {
    event.preventDefault()
    this.showPhoneStep()
  }

  sendOtp(event) {
    event.preventDefault()
    const phone = this.phoneTarget.value.replace(/\D/g, "")
    const countryCode = this.hasCountryCodeTarget ? this.countryCodeTarget.value.trim() : ""
    if (!phone) { this._error(this.phoneErrorTarget, "Please enter your phone number."); return }

    this._loading(this.submitPhoneTarget, true)
    fetch("/users/send_otp", {
      method: "POST",
      headers: this._headers(),
      body: JSON.stringify({ phone, country_code: countryCode, return_to: this.returnTo })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.success) {
          this.fullPhone = data.phone
          if (this.hasPhoneLabelTarget) this.phoneLabelTarget.textContent = data.phone
          this.showOtpStep()
        } else {
          this._error(this.phoneErrorTarget, data.error || "Could not send OTP. Try again.")
        }
      })
      .catch(() => this._error(this.phoneErrorTarget, "Something went wrong. Please try again."))
      .finally(() => this._loading(this.submitPhoneTarget, false))
  }

  resend(event) {
    event.preventDefault()
    this.sendOtp(event)
  }

  otpInput(event) {
    const input = event.target
    const cleanValue = input.value.replace(/\D/g, "")

    if (cleanValue.length > 1) {
      // User pasted or typed multiple digits (handles password managers and native autofill gracefully)
      this.otpDigitTargets.forEach((el, i) => {
        el.value = cleanValue[i] || ""
      })
      const nextFocus = Math.min(cleanValue.length, this.otpDigitTargets.length - 1)
      this.otpDigitTargets[nextFocus].focus()
      return
    }

    input.value = cleanValue.slice(0, 1)
    const idx = this.otpDigitTargets.indexOf(input)
    if (input.value && idx < this.otpDigitTargets.length - 1) {
      this.otpDigitTargets[idx + 1].focus()
    }
  }

  otpKeydown(event) {
    if (event.key === "Backspace" && !event.target.value) {
      const idx = this.otpDigitTargets.indexOf(event.target)
      if (idx > 0) this.otpDigitTargets[idx - 1].focus()
    }
  }

  verifyOtp(event) {
    event.preventDefault()
    const code = this.otpDigitTargets.map(i => i.value).join("")
    if (code.length < this.otpDigitTargets.length) { this._error(this.otpErrorTarget, "Please enter the full OTP."); return }

    this._loading(this.submitOtpTarget, true)
    fetch("/users/verify_otp", {
      method: "POST",
      headers: this._headers(),
      body: JSON.stringify({ otp_code_combined: code, phone: this.fullPhone, return_to: this.returnTo })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.success) {
          window.location.href = data.redirect || window.location.href
        } else if (data.expired) {
          this.showPhoneStep()
          this._error(this.phoneErrorTarget, data.error || "OTP expired. Request a new one.")
        } else {
          this._error(this.otpErrorTarget, data.error || "Invalid OTP.")
        }
      })
      .catch(() => this._error(this.otpErrorTarget, "Something went wrong. Please try again."))
      .finally(() => this._loading(this.submitOtpTarget, false))
  }

  _headers() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": meta ? meta.content : ""
    }
  }

  _clearErrors() {
    [this.hasPhoneErrorTarget && this.phoneErrorTarget, this.hasOtpErrorTarget && this.otpErrorTarget].forEach(t => {
      if (t) { t.textContent = ""; t.classList.add("hidden") }
    })
  }

  _error(target, message) {
    if (!target) return
    target.textContent = message
    target.classList.remove("hidden")
  }

  _loading(btn, on) {
    if (!btn) return
    btn.disabled = on
    btn.classList.toggle("opacity-75", on)
    btn.classList.toggle("pointer-events-none", on)

    const spinner = btn.querySelector(".animate-spin")
    const icon = btn.querySelector("svg:not(.animate-spin)")

    if (spinner) {
      spinner.classList.toggle("hidden", !on)
    }
    if (icon) {
      icon.classList.toggle("hidden", on)
    }
  }
}
